import { localize2WithPath, localizeWithPath } from 'vscode/vscode/vs/nls';
import { Action2, MenuId, registerAction2 } from 'vscode/vscode/vs/platform/actions/common/actions';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { NotebookOutlineContext } from '../contrib/outline/notebookOutline.js';
import { FoldingController } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/controller/foldingController';
import { executeIcon } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/notebookIcons';
import { CellKind } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookCommon';

const _moduleId = "vs/workbench/contrib/notebook/browser/controller/sectionActions";
class NotebookRunSingleCellInSection extends Action2 {
    constructor() {
        super({
            id: 'notebook.section.runSingleCell',
            title: {
                ...( localize2WithPath(_moduleId, 0, "Run Cell")),
                mnemonicTitle: ( localizeWithPath(_moduleId, 1, "&&Run Cell")),
            },
            shortTitle: ( localizeWithPath(_moduleId, 0, "Run Cell")),
            icon: executeIcon,
            menu: [
                {
                    id: MenuId.NotebookOutlineActionMenu,
                    group: 'inline',
                    order: 1,
                    when: ( (ContextKeyExpr.and(
                         (NotebookOutlineContext.CellKind.isEqualTo(CellKind.Code)),
                         (NotebookOutlineContext.OutlineElementTarget.isEqualTo(1 )),
                         (NotebookOutlineContext.CellHasChildren.toNegated()),
                         (NotebookOutlineContext.CellHasHeader.toNegated())
                    )))
                }
            ]
        });
    }
    async run(_accessor, context) {
        if (!checkSectionContext(context)) {
            return;
        }
        context.notebookEditor.executeNotebookCells([context.outlineEntry.cell]);
    }
}
class NotebookRunCellsInSection extends Action2 {
    constructor() {
        super({
            id: 'notebook.section.runCells',
            title: {
                ...( localize2WithPath(_moduleId, 2, "Run Cells In Section")),
                mnemonicTitle: ( localizeWithPath(_moduleId, 3, "&&Run Cells In Section")),
            },
            shortTitle: ( localizeWithPath(_moduleId, 2, "Run Cells In Section")),
            menu: [
                {
                    id: MenuId.NotebookStickyScrollContext,
                    group: 'notebookExecution',
                    order: 1
                },
                {
                    id: MenuId.NotebookOutlineActionMenu,
                    group: 'inline',
                    order: 1,
                    when: ( (ContextKeyExpr.and(
                         (NotebookOutlineContext.CellKind.isEqualTo(CellKind.Markup)),
                         (NotebookOutlineContext.OutlineElementTarget.isEqualTo(1 )),
                        NotebookOutlineContext.CellHasChildren,
                        NotebookOutlineContext.CellHasHeader
                    )))
                }
            ]
        });
    }
    async run(_accessor, context) {
        if (!checkSectionContext(context)) {
            return;
        }
        const cell = context.outlineEntry.cell;
        const idx = context.notebookEditor.getViewModel()?.getCellIndex(cell);
        if (idx === undefined) {
            return;
        }
        const length = context.notebookEditor.getViewModel()?.getFoldedLength(idx);
        if (length === undefined) {
            return;
        }
        const cells = context.notebookEditor.getCellsInRange({ start: idx, end: idx + length + 1 });
        context.notebookEditor.executeNotebookCells(cells);
    }
}
class NotebookFoldSection extends Action2 {
    constructor() {
        super({
            id: 'notebook.section.foldSection',
            title: {
                ...( localize2WithPath(_moduleId, 4, "Fold Section")),
                mnemonicTitle: ( localizeWithPath(_moduleId, 5, "&&Fold Section")),
            },
            shortTitle: ( localizeWithPath(_moduleId, 4, "Fold Section")),
            menu: [
                {
                    id: MenuId.NotebookOutlineActionMenu,
                    group: 'notebookFolding',
                    order: 2,
                    when: ( (ContextKeyExpr.and(
                         (NotebookOutlineContext.CellKind.isEqualTo(CellKind.Markup)),
                         (NotebookOutlineContext.OutlineElementTarget.isEqualTo(1 )),
                        NotebookOutlineContext.CellHasChildren,
                        NotebookOutlineContext.CellHasHeader,
                         (NotebookOutlineContext.CellFoldingState.isEqualTo(1 ))
                    )))
                }
            ]
        });
    }
    async run(_accessor, context) {
        if (!checkSectionContext(context)) {
            return;
        }
        this.toggleFoldRange(context.outlineEntry, context.notebookEditor);
    }
    toggleFoldRange(entry, notebookEditor) {
        const foldingController = notebookEditor.getContribution(FoldingController.id);
        const index = entry.index;
        const headerLevel = entry.level;
        const newFoldingState = 2 ;
        foldingController.setFoldingStateDown(index, newFoldingState, headerLevel);
    }
}
class NotebookExpandSection extends Action2 {
    constructor() {
        super({
            id: 'notebook.section.expandSection',
            title: {
                ...( localize2WithPath(_moduleId, 6, "Expand Section")),
                mnemonicTitle: ( localizeWithPath(_moduleId, 7, "&&Expand Section")),
            },
            shortTitle: ( localizeWithPath(_moduleId, 6, "Expand Section")),
            menu: [
                {
                    id: MenuId.NotebookOutlineActionMenu,
                    group: 'notebookFolding',
                    order: 2,
                    when: ( (ContextKeyExpr.and(
                         (NotebookOutlineContext.CellKind.isEqualTo(CellKind.Markup)),
                         (NotebookOutlineContext.OutlineElementTarget.isEqualTo(1 )),
                        NotebookOutlineContext.CellHasChildren,
                        NotebookOutlineContext.CellHasHeader,
                         (NotebookOutlineContext.CellFoldingState.isEqualTo(2 ))
                    )))
                }
            ]
        });
    }
    async run(_accessor, context) {
        if (!checkSectionContext(context)) {
            return;
        }
        this.toggleFoldRange(context.outlineEntry, context.notebookEditor);
    }
    toggleFoldRange(entry, notebookEditor) {
        const foldingController = notebookEditor.getContribution(FoldingController.id);
        const index = entry.index;
        const headerLevel = entry.level;
        const newFoldingState = 1 ;
        foldingController.setFoldingStateDown(index, newFoldingState, headerLevel);
    }
}
function checkSectionContext(context) {
    return !!(context && context.notebookEditor && context.outlineEntry);
}
registerAction2(NotebookRunSingleCellInSection);
registerAction2(NotebookRunCellsInSection);
registerAction2(NotebookFoldSection);
registerAction2(NotebookExpandSection);

export { NotebookExpandSection, NotebookFoldSection, NotebookRunCellsInSection, NotebookRunSingleCellInSection };
